/**
 * Import a recipe from a link. Paste a URL, we fetch the page and let the assistant
 * pull out the ingredients and steps, then hand it to the editor to review before
 * anything is saved. A failed import keeps the link on screen and says plainly what
 * went wrong — AI not connected, page wouldn't load, or no recipe found — so nobody
 * loses what they typed or is left guessing.
 */
import { useState } from 'react';
import type { ParsedRecipe } from '../../shared/types';
import { errMsg, mealsApi } from './mealsApi';
import { ErrorNote, Modal, btnPrimary, btnSecondary, inputCls, labelCls } from './ui';

export default function RecipeImport({
  onParsed,
  onClose,
}: {
  onParsed: (recipe: ParsedRecipe) => void;
  onClose: () => void;
}) {
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function run(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = url.trim();
    if (!trimmed) {
      setError('Paste the link to the recipe first.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const parsed = await mealsApi.importRecipe(trimmed);
      onParsed(parsed); // parent opens the editor pre-filled; the URL stays if it doesn't
    } catch (err) {
      // Keep the URL exactly as typed and explain — never a blank field, never a shrug.
      setError(errMsg(err));
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Import from a link"
      onClose={onClose}
      footer={
        <div className="flex items-center justify-end gap-2">
          <button type="button" className={btnSecondary} onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" form="recipe-import-form" className={btnPrimary} disabled={busy}>
            {busy ? 'Reading…' : 'Find recipe'}
          </button>
        </div>
      }
    >
      <form id="recipe-import-form" onSubmit={run} className="space-y-4">
        <p className="text-sm text-ink-soft">
          Paste a link to a recipe page and we'll read the ingredients and steps for you. You'll get
          to check it over and fix anything before it's saved.
        </p>

        {error && <ErrorNote message={error} />}

        <div>
          <label className={labelCls} htmlFor="recipe-url">
            Recipe link
          </label>
          <input
            id="recipe-url"
            type="url"
            inputMode="url"
            className={inputCls}
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://www.example.com/best-chili"
            autoComplete="off"
            disabled={busy}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
          />
        </div>

        {busy && (
          <p role="status" aria-live="polite" className="text-sm text-ink-faint">
            Opening the page and reading the recipe — this can take a few seconds.
          </p>
        )}
      </form>
    </Modal>
  );
}
